import { ListPlus, Lock, TrendingUp, Gift, Coins, BarChart3, Link as LinkIcon, ExternalLink } from "lucide-react";

const DexButton = ({ testId }) => (
  <a
    href="https://autonodex.org/"
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="btn-electric inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
  >
    AUTONO DEX <ExternalLink className="w-4 h-4" />
  </a>
);

const Section = ({ emoji, title, subtitle, children }) => (
  <div className="mt-8 first:mt-0">
    <h4 className="font-heading font-bold text-lg sm:text-xl text-white flex items-center gap-2.5">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="text-gradient-accent">{title}</span>
    </h4>
    {subtitle && (
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        {subtitle}
      </p>
    )}
    <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const steps = [
  { t: "Complete the Listing Form", d: "Provide the required information about your token and project." },
  { t: "Pay the Listing Fee", d: "Review and pay the applicable Autono Dex listing fee." },
  { t: "Submit Your Listing", d: "Once submitted, your token is automatically processed for listing." },
  { t: "You're Listed 🚀", d: "Your token becomes available within the Autono Dex ecosystem without requiring manual approval." },
];

const tools = [
  { icon: Lock, label: "Token Lockers" },
  { icon: TrendingUp, label: "Staking" },
  { icon: Gift, label: "Airdrops" },
  { icon: Coins, label: "Token tools" },
  { icon: BarChart3, label: "Project utilities" },
  { icon: LinkIcon, label: "Other DeFi tools" },
];

export const ListDetail = () => {
  return (
    <div data-testid="list-detail-content">
      <div className="mb-6">
        <DexButton testId="list-dex-top" />
      </div>
      <Section emoji="🪙" title="Token Listings" subtitle="Listing Has Never Been Easier">
        <p>
          With Autono Dex, listing a token is designed to be fast, autonomous, and permissionless.
        </p>
        <p>
          Forget lengthy application processes and waiting for manual approval. Once your token meets
          the required listing criteria, you can submit your listing directly through Autono Dex and
          let the platform handle the process.
        </p>
      </Section>

      <Section emoji="⚡" title="How It Works">
        <div className="space-y-3 pt-1">
          {steps.map((s, i) => (
            <div key={s.t} className="flex items-start gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-600/30 border border-white/10 flex items-center justify-center font-mono text-xs font-bold text-cyan-300">
                {i + 1}
              </span>
              <div>
                <p className="font-semibold text-white text-sm">{s.t}</p>
                <p className="text-sm text-slate-400">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="font-semibold text-white pt-1">
          Permissionless by design. Autonomous by default.
        </p>
      </Section>

      <Section emoji="🛡️" title="Verified Token List">
        <p>Being listed on Autono Dex does not automatically mean a project is verified.</p>
        <p>
          If you'd like your project considered for our Verified Token List, contact the Autono Dex
          team for information about the verification process and requirements.
        </p>
        <p className="text-slate-400">
          A verified designation is intended to help users distinguish projects that have gone
          through Autono Dex's additional verification process.
        </p>
      </Section>

      <Section emoji="👨‍💻" title="Developer Profiles">
        <p>When you list a token, you'll be prompted to create your Developer Profile.</p>
        <p>
          Once your profile is established, your account will receive a{" "}
          <span className="text-white font-semibold">Dev Badge</span> identifying you as the
          developer / project owner associated with your project.
        </p>
        <p className="font-semibold text-white pt-1">
          Your Dev Profile Gives You Access to Project Tools
        </p>
        <p>
          Your developer account can be used to manage and configure the tools available for your
          project within Autono Dex. Developers can choose which supported Autono Dex tools they want
          to make available to their project's community, such as:
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          {tools.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.label}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5"
              >
                <Icon className="w-4 h-4 text-cyan-400 shrink-0" />
                <span className="text-xs text-slate-200">{t.label}</span>
              </div>
            );
          })}
        </div>
      </Section>

      <Section emoji="🔐" title="Project-Controlled Access">
        <p>
          Users can only access the project tools that the developer has enabled for that project.
        </p>
        <p className="text-slate-400">
          This gives project creators greater control over how their Autono Dex project ecosystem is
          presented and which tools their community can use.
        </p>
      </Section>

      <Section emoji="🚀" title="Built for Permissionless DeFi">
        <p>
          Autono Dex is designed to remove unnecessary barriers between creators and decentralized
          finance.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {[
            "Create your project",
            "List your token",
            "Build your developer profile",
            "Choose your tools",
            "Grow your ecosystem",
          ].map((x) => (
            <span
              key={x}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {x}
            </span>
          ))}
        </div>
        <p className="text-slate-400">No long approval queues. No unnecessary waiting.</p>
      </Section>

      <div className="mt-8 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-purple-600/5 p-5 flex items-start gap-3">
        <ListPlus className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-heading font-bold text-white">Autono Dex</p>
          <p className="text-sm text-gradient-accent font-semibold">
            Autonomous Intelligence. Decentralized Finance.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <DexButton testId="list-dex-bottom" />
      </div>
    </div>
  );
};
